"use client";

import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "@/components/icons";
import { cn } from "@/lib/utils";
import {
  ACCEPTED_UPLOAD_FORMAT_LABEL,
  MAX_PHOTOS,
  UPLOAD_ACCEPT_ATTRIBUTE,
} from "@/lib/photos/constants";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import type { Photo } from "@/lib/products/types";
import type { Id } from "@convex/_generated/dataModel";
import { IconButton } from "@/components/ui/icon-button";
import { SelectableCard } from "@/components/ui/selectable-card";
import { PhotoFileInput } from "@/components/photos/photo-file-input";
import { useCondensedPhotoFiles } from "./use-condensed-photo-files";

interface CondensedPhotoUploadProps {
  photos: Photo[];
  onPhotosChange: (photos: Photo[]) => void;
  generateUploadUrl?: () => Promise<string>;
  attestUpload?: (storageId: Id<"_storage">) => Promise<unknown>;
  className?: string;
}

export function CondensedPhotoUpload({
  photos,
  onPhotosChange,
  generateUploadUrl,
  attestUpload,
  className,
}: CondensedPhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { addFiles, removePhoto, isUploading, error, setError } =
    useCondensedPhotoFiles({
      photos,
      onPhotosChange,
      generateUploadUrl,
      attestUpload,
    });
  const atCapacity = photos.length >= MAX_PHOTOS;

  return (
    <div className={cn("space-y-3", className)}>
      <PhotoFileInput
        ref={inputRef}
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        multiple
        onChange={(event) => {
          void addFiles(event.target.files);
          event.target.value = "";
        }}
      />

      {photos.length > 0 && (
        <CondensedPhotoGrid photos={photos} onRemove={removePhoto} />
      )}

      {!atCapacity && (
        <CondensedPhotoDropCard
          photoCount={photos.length}
          isUploading={isUploading}
          onBrowse={() => inputRef.current?.click()}
          onFilesDropped={(files) => void addFiles(files)}
        />
      )}
      <ActionErrorAlert
        error={error}
        reportable={false}
        onDismiss={() => setError(null)}
      />
    </div>
  );
}

function CondensedPhotoGrid({
  photos,
  onRemove,
}: {
  photos: Photo[];
  onRemove: (photo: Photo) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {photos.map((photo, index) => (
        <div
          key={photo.storageId}
          className="group relative aspect-square overflow-hidden rounded-lg border bg-muted bg-cover bg-center"
          style={{ backgroundImage: `url(${photo.url})` }}
        >
          {index === 0 && (
            <span className="absolute left-1.5 top-1.5 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-medium text-background">
              Cover
            </span>
          )}
          <IconButton
            size="compact"
            aria-label="Remove photo"
            onClick={() => onRemove(photo)}
            className="absolute right-1.5 top-1.5 rounded-full bg-background/90 text-muted-foreground shadow-sm hover:text-destructive-strong reveal-on-hover"
          >
            <X />
          </IconButton>
        </div>
      ))}
    </div>
  );
}

function CondensedPhotoDropCard({
  photoCount,
  isUploading,
  onBrowse,
  onFilesDropped,
}: {
  photoCount: number;
  isUploading: boolean;
  onBrowse: () => void;
  onFilesDropped: (files: FileList) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  return (
    <SelectableCard
      isSelected={isDragOver}
      onClick={onBrowse}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragOver(false);
        onFilesDropped(event.dataTransfer.files);
      }}
      className="w-full gap-2 border-dashed px-6 py-8"
    >
      <DropCardLabel photoCount={photoCount} isUploading={isUploading} />
    </SelectableCard>
  );
}

function DropCardLabel({
  photoCount,
  isUploading,
}: {
  photoCount: number;
  isUploading: boolean;
}) {
  return (
    <>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {isUploading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <ImagePlus className="h-5 w-5" />
        )}
      </span>
      <span className="text-sm font-medium text-foreground">
        {photoCount === 0
          ? "Drag & drop photos, or click to upload"
          : "Add more photos"}
      </span>
      <span className="text-xs text-muted-foreground">
        {photoCount === 0
          ? `Up to ${MAX_PHOTOS} · ${ACCEPTED_UPLOAD_FORMAT_LABEL}`
          : `${photoCount} of ${MAX_PHOTOS} added`}
      </span>
    </>
  );
}
